import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Link } from '@flightstats/airframe-react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import {
  TripGroupAndAncestorsView,
  AgentEmailWatcher,
  TripGroupView,
  TripGroupViewSettings,
} from '../../types/tripGroup';
import { BodyCard } from '../components/styledComponent';
import { TripAssistSection } from './TripAssistSection';
import { TripGroupInfoSection } from './TripGroupInfoSection';
import { StyledSwitch } from '../../components/forms/Switch';
import { TripAlertEmails } from './TripAlertEmails';
import { TripGroupEmailConfiguration } from './TripGroupEmailConfiguration';
import { TravelerTripAlertsSection } from './TravelerTripAlertsSection';
import FormButtonRow from '../components/formButtonRow';
import type { MessageConfiguration } from '../../types/messageConfiguration';
import { getProperty } from '../../fsConfig';

const LeftPaddingWrapper = styled.div`
  padding-left: 15px;
`;

type CustomizedTextProps = {
  isOverridden: boolean;
  ancestorTripGroupViewForAlertProfile?: TripGroupView | null;
};

const CustomizedText: React.FC<CustomizedTextProps> = ({
  isOverridden,
  ancestorTripGroupViewForAlertProfile,
}) => {
  if (isOverridden) {
    return (
      <>
        Un-customizing trip groups will remove the current settings permanently.
      </>
    );
  } else if (ancestorTripGroupViewForAlertProfile) {
    return (
      <span className="fs-exclude" data-fs-exclude>
        This configuration is inherited from{' '}
        <Link href={`/tripGroups/${ancestorTripGroupViewForAlertProfile.id}`}>
          {ancestorTripGroupViewForAlertProfile.lineage} (
          {ancestorTripGroupViewForAlertProfile.tripGroupViewSettings.name})
        </Link>
      </span>
    );
  } else {
    return <></>;
  }
};

type EditTripGroupFormProps = {
  tripGroup: TripGroupAndAncestorsView;
  saveSettings: (settings: TripGroupViewSettings) => void;
  messageConfigs: MessageConfiguration[];
};

const defaultTripSettings = {
  createIssues: false,
  createInvalidTripIssues: false,
  requireOptedInTravelerProfilesForIssueCreation: false,
};

const defaultTravelerAlerts = {
  sendTravelerAlerts: false,
  sendTripReminderAlerts: false,
};

const noneSelected = 'none';

export type EmailType = AgentEmailWatcher & { key: string };

export const EditTripGroupForm: React.FC<EditTripGroupFormProps> = ({
  tripGroup,
  saveSettings,
  messageConfigs,
}) => {
  const { lineage, tripGroupViewSettings } = tripGroup.tripGroupView;
  const alertProfileView = tripGroupViewSettings.alertProfileView;
  const inheritedAlertProfileView =
    tripGroup.ancestorTripGroupViewForAlertProfile?.tripGroupViewSettings
      ?.alertProfileView;
  const travelerAlerts = alertProfileView?.travelerAlertSettings;
  const gdsQueueTrips = !!tripGroupViewSettings.tripAcquisitionView
    ?.importGdsQueueTrips;

  const user = getProperty('session', {});
  const [tripAlertEmailIssue, setTripAlertEmailIssue] = useState(false);
  const [isOverridden, setIsOverridden] = useState(true);
  const [name, setName] = useState(tripGroupViewSettings.name);
  const [isHidden, setIsHidden] = useState(tripGroupViewSettings.hidden);
  const [tripAssistSettings, setTripAssistSettings] = useState(
    Object.assign({}, alertProfileView?.tripAssistSettings),
  );
  const [emailData, setEmailData] = useState<EmailType[]>([]);
  const [travelerAlertSettings, setTravelerAlertSettings] = useState(
    Object.assign({}, travelerAlerts),
  );
  const [importGdsQueueTrips, setImportGdsQueueTrips] = useState(gdsQueueTrips);
  // email config consts
  const [messageConfigId, setMessageConfigId] = useState(
    tripGroupViewSettings.emailConfigurationTravelerView?.id ?? '',
  );
  const [travelerIsOverridden, setTravelerIsOverridden] = useState(
    !!tripGroupViewSettings.emailConfigurationTravelerView,
  );
  const [atRiskMessageConfigId, setAtRiskMessageConfigId] = useState(
    tripGroupViewSettings.emailConfigurationAtRiskView?.id ?? '',
  );
  const [agentIsOverridden, setAgentIsOverridden] = useState(
    !!tripGroupViewSettings.emailConfigurationAtRiskView,
  );

  const canViewEmails = typeof user.canViewEmailConfig === 'function' ? user.canViewEmailConfig() : false;
  const isDefaultGroup = !tripGroup.ancestorTripGroupViewForAlertProfile;
  const settings = isOverridden
    ? tripAssistSettings
    : inheritedAlertProfileView?.tripAssistSettings ?? defaultTripSettings;
  const displayTravelerAlerts = isOverridden
    ? travelerAlertSettings
    : inheritedAlertProfileView?.travelerAlertSettings ?? defaultTravelerAlerts;
  const travelerTripGroup = travelerIsOverridden
    ? tripGroup.tripGroupView
    : tripGroup.ancestorTripGroupViewForEmailConfigurationTraveler;
  const atRiskTripGroup = agentIsOverridden
    ? tripGroup.tripGroupView
    : tripGroup.ancestorTripGroupViewForEmailConfigAtRisk;

  const onSave = () => {
    const newTripSettings: TripGroupViewSettings = {
      name: name,
      hidden: isHidden,
    };
    const filteredEmails = emailData.filter(item => item.emailAddress); // filter out null/empty entries

    isOverridden &&
      (newTripSettings.alertProfileView = {
        travelerAlertSettings,
        tripAssistSettings,
        agentAlertSettings: {
          agentEmailWatchers: filteredEmails.map(data => {
            const email: AgentEmailWatcher = {
              emailAddress: data.emailAddress,
              active: data.active,
            };
            return email;
          }),
        },
      });

    if (canViewEmails && travelerIsOverridden) {
      newTripSettings.emailConfigurationTravelerView = {
        id: messageConfigId,
        name: '',
      };
    }

    if (canViewEmails && agentIsOverridden) {
      newTripSettings.emailConfigurationAtRiskView = {
        id: atRiskMessageConfigId,
        name: '',
      };
    }

    if (tripGroupViewSettings.tripAcquisitionView) {
      newTripSettings.tripAcquisitionView = {
        importGdsQueueTrips: importGdsQueueTrips,
      };
    }
    saveSettings(newTripSettings);
    if (isOverridden) {
      setEmailData(filteredEmails);
    }
  };

  const revertHandler = () => {
    setIsOverridden(!!alertProfileView);
    setName(tripGroupViewSettings.name);
    setIsHidden(tripGroupViewSettings.hidden);
    setMessageConfigId(
      tripGroupViewSettings.emailConfigurationTravelerView?.id ?? '',
    );
    setTravelerIsOverridden(
      !!tripGroupViewSettings.emailConfigurationTravelerView,
    );
    setAtRiskMessageConfigId(
      tripGroupViewSettings.emailConfigurationAtRiskView?.id ?? '',
    );
    setAgentIsOverridden(!!tripGroupViewSettings.emailConfigurationAtRiskView);
    setImportGdsQueueTrips(gdsQueueTrips);
    setTripAssistSettings(
      Object.assign({}, alertProfileView?.tripAssistSettings),
    );
    updateEmails();
    setTravelerAlertSettings(Object.assign({}, travelerAlerts));
  };

  const updateEmails = () => {
    const profileView = isOverridden
      ? alertProfileView
      : inheritedAlertProfileView;
    setEmailData(
      profileView?.agentAlertSettings?.agentEmailWatchers?.map(item => {
        const obj: EmailType = { key: uuidv4(), ...item };
        return obj;
      }) ?? [],
    );
  };

  useEffect(() => {
    revertHandler();
  }, [tripGroup]);

  return (
    <>
      <BodyCard>
        <LeftPaddingWrapper>
          <TripGroupInfoSection
            lineage={lineage}
            groupName={name}
            setGroupName={setName}
            isHidden={isHidden}
            setIsHidden={setIsHidden}
            isDefault={isDefaultGroup}
          />
        </LeftPaddingWrapper>
      </BodyCard>
      {tripGroupViewSettings.tripAcquisitionView && (
        <BodyCard>
          <LeftPaddingWrapper>
            <h4>Trip Acquisition</h4>
            <StyledSwitch
              text="Import GDS Queue Trips"
              helperText="Enable the import of Trips from a GDS Queue source for this lineage"
              on={importGdsQueueTrips}
              onChange={(val: boolean) => {
                setImportGdsQueueTrips(val);
              }}
            />
          </LeftPaddingWrapper>
        </BodyCard>
      )}
      {canViewEmails && ( // traveler
        <BodyCard>
          <LeftPaddingWrapper>
            <TripGroupEmailConfiguration
              tripGroupView={travelerTripGroup}
              emailConfig={messageConfigs.find(config => {
                return messageConfigId === config.id;
              })}
              onSelect={setMessageConfigId}
              messageConfigs={messageConfigs}
              isOverridden={travelerIsOverridden || isDefaultGroup}
              setIsOverridden={setTravelerIsOverridden}
              labelType="Traveler Trip"
              isDisabled={isDefaultGroup}
            ></TripGroupEmailConfiguration>
          </LeftPaddingWrapper>
        </BodyCard>
      )}
      {canViewEmails &&
        (agentIsOverridden ||
          tripGroup.ancestorTripGroupViewForEmailConfigAtRisk) && ( // agent
          <BodyCard>
            <LeftPaddingWrapper>
              <TripGroupEmailConfiguration
                tripGroupView={atRiskTripGroup}
                emailConfig={messageConfigs.find(config => {
                  return atRiskMessageConfigId === config.id;
                })}
                messageConfigs={messageConfigs}
                onSelect={setAtRiskMessageConfigId}
                isOverridden={agentIsOverridden || isDefaultGroup}
                setIsOverridden={setAgentIsOverridden}
                labelType="Agent"
                isDisabled={
                  !tripGroup.ancestorTripGroupViewForEmailConfigAtRisk
                }
              ></TripGroupEmailConfiguration>
            </LeftPaddingWrapper>
          </BodyCard>
        )}
      <BodyCard>
        <LeftPaddingWrapper>
          <Row>
            <Col align={'flex-end'}>
              <StyledSwitch
                text="Customize Trip Group Settings"
                name="customOverride"
                isDisabled={isDefaultGroup}
                on={isOverridden}
                onChange={setIsOverridden}
                textRight={false}
              />
            </Col>
          </Row>
          <Col align={'flex-end'} style={{ paddingRight: 0 }}>
            <small>
              <CustomizedText
                isOverridden={isOverridden}
                ancestorTripGroupViewForAlertProfile={
                  tripGroup.ancestorTripGroupViewForAlertProfile
                }
              />
            </small>
          </Col>
          <TripAssistSection
            tripAssistSettings={settings}
            setTripAssistSettings={setTripAssistSettings}
            isOverridden={isOverridden && !isHidden}
          />
          <TripAlertEmails
            emailData={emailData}
            setEmailData={setEmailData}
            isOverridden={isOverridden && !isHidden}
            setSaveDisabled={setTripAlertEmailIssue}
          />
          <TravelerTripAlertsSection
            travelerTripAlertSettings={displayTravelerAlerts}
            setTravelerAlertSettings={setTravelerAlertSettings}
            isOverridden={isOverridden && !isHidden}
          />
        </LeftPaddingWrapper>
      </BodyCard>
      <FormButtonRow
        onClear={revertHandler}
        onSubmit={onSave}
        saveDisabled={
          tripAlertEmailIssue ||
          !messageConfigs.length ||
          messageConfigId === noneSelected ||
          atRiskMessageConfigId === noneSelected
        }
        clearText="REVERT"
        submitText="SAVE"
      />
    </>
  );
};
